import { useState, useEffect, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Lock, Copy, Check, Send, Loader2, MessageCircle, Euro, LogOut, RefreshCw, Clock, XCircle, FileText, Search, Package, ChevronDown, ChevronUp, Zap, Pencil, Trash2, Plus, X, Save, Truck } from "lucide-react";
import { toast } from "sonner";
import { useSeoMeta } from "../i18n/LanguageContext";
import { waTemplates } from "../i18n/translations";

// Customer-facing WhatsApp messages follow the language the order was placed in (fallback: English)
const waT = (lang) => waTemplates[lang] || waTemplates.en;

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

export default function PaymentAdminPage() {
  useSeoMeta({
    title: "Area Riservata | Locker Room",
    description: "Area riservata alla gestione degli ordini e dei pagamenti Locker Room.",
    noindex: true,
  });
  const [authed, setAuthed] = useState(false);
  const [password, setPassword] = useState("");
  const [pwError, setPwError] = useState("");
  const [logging, setLogging] = useState(false);

  // Form state
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [feePercent, setFeePercent] = useState("3.5");
  const [customFee, setCustomFee] = useState(false);
  const [feePreview, setFeePreview] = useState({ service_fee: 0, total_amount: 0, service_fee_percent: 3.5 });

  // Result state
  const [link, setLink] = useState("");
  const [tapLink, setTapLink] = useState("");
  const [linkAmount, setLinkAmount] = useState("");
  const [linkFee, setLinkFee] = useState("");
  const [linkTotal, setLinkTotal] = useState("");
  const [clientRef, setClientRef] = useState("");
  const [linkType, setLinkType] = useState("payment");
  const [linkLang, setLinkLang] = useState("en");
  const [copied, setCopied] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // History state
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyQuery, setHistoryQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Orders archive state (WhatsApp submissions)
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [specialRequests, setSpecialRequests] = useState([]);
  const [ordersQuery, setOrdersQuery] = useState("");
  const [ordersStatusFilter, setOrdersStatusFilter] = useState("all");
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [generatingFor, setGeneratingFor] = useState(null);
  const [editingOrder, setEditingOrder] = useState(null);
  const [deletingOrder, setDeletingOrder] = useState(null);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [deletingInvoice, setDeletingInvoice] = useState(null);
  const formRef = useRef(null);

  const fetchHistory = useCallback(async (pw) => {
    if (!pw) return;
    setHistoryLoading(true);
    try {
      const { data } = await axios.post(`${API}/payment/admin/list`, { password: pw });
      setHistory(data.items || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        sessionStorage.removeItem("lr_admin_pw");
        setAuthed(false);
        toast.error("Sessione scaduta");
      }
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const fetchOrders = useCallback(async (pw) => {
    if (!pw) return;
    setOrdersLoading(true);
    try {
      const { data } = await axios.post(`${API}/orders/admin/list`, { password: pw });
      setOrders(data.items || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        sessionStorage.removeItem("lr_admin_pw");
        setAuthed(false);
      }
    } finally {
      setOrdersLoading(false);
    }
    // Special (custom) order requests — separate section, no payment
    try {
      const { data } = await axios.post(`${API}/special-requests/admin/list`, { password: pw });
      setSpecialRequests(data.items || []);
    } catch { /* keep existing list on transient errors */ }
  }, []);

  const handleUpdateSpecialStatus = async (reqId, status) => {
    try {
      await axios.post(`${API}/special-requests/${reqId}/update`, { password, status });
      setSpecialRequests((prev) => prev.map((r) => (r.id === reqId ? { ...r, status } : r)));
    } catch {
      toast.error("Errore aggiornamento stato");
    }
  };

  const handleDeleteSpecial = async (reqId) => {
    try {
      await axios.post(`${API}/special-requests/${reqId}/delete`, { password });
      setSpecialRequests((prev) => prev.filter((r) => r.id !== reqId));
      toast.success("Richiesta eliminata");
    } catch {
      toast.error("Errore durante l'eliminazione");
    }
  };

  const specialStatusMeta = (s) => {
    if (s === "DONE") return { label: "Completato", cls: "bg-[#009A49]/20 text-[#009A49] border-[#009A49]/40" };
    if (s === "CONTACTED") return { label: "Contattato", cls: "bg-blue-500/15 text-blue-400 border-blue-500/40" };
    return { label: "Nuovo", cls: "bg-amber-500/10 text-amber-400 border-amber-500/30" };
  };

  useEffect(() => {
    const saved = sessionStorage.getItem("lr_admin_pw");
    if (saved) {
      setPassword(saved);
      setAuthed(true);
      fetchHistory(saved);
      fetchOrders(saved);
    }
  }, [fetchHistory, fetchOrders]);

  // Auto-refresh history + orders every 20s
  useEffect(() => {
    if (!authed) return;
    const id = setInterval(() => { fetchHistory(password); fetchOrders(password); }, 20000);
    return () => clearInterval(id);
  }, [authed, password, fetchHistory, fetchOrders]);

  // Live service fee preview (debounced) — recomputes when amount OR fee % changes
  useEffect(() => {
    const t = setTimeout(async () => {
      const base = parseFloat(amount);
      const pct = parseFloat(feePercent);
      if (!base || base <= 0) {
        setFeePreview({ service_fee: 0, total_amount: 0, service_fee_percent: pct });
        return;
      }
      try {
        const { data } = await axios.post(`${API}/payment/preview-fee`, { base_amount_eur: base, fee_percent_override: pct });
        setFeePreview(data);
      } catch { /* ignore preview failures */ }
    }, 200);
    return () => clearTimeout(t);
  }, [amount, feePercent]);

  const handleUnlock = async (e) => {
    e.preventDefault();
    if (!password.trim()) {
      setPwError("Inserisci la password");
      return;
    }
    setLogging(true);
    try {
      await axios.post(`${API}/payment/admin/login`, { password });
      sessionStorage.setItem("lr_admin_pw", password);
      setAuthed(true);
      setPwError("");
      fetchHistory(password);
      fetchOrders(password);
    } catch (err) {
      if (err?.response?.status === 429) setPwError("Troppi tentativi. Riprova tra qualche minuto.");
      else setPwError("Password non valida");
    } finally {
      setLogging(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem("lr_admin_pw");
    setPassword("");
    setAuthed(false);
    setLink("");
    setHistory([]);
    setOrders([]);
  };

  const resetForm = () => {
    setAmount("");
    setDescription("");
    setCustomerName("");
    setCustomerPhone("");
    setCustomerEmail("");
    setLink("");
    setTapLink("");
    setClientRef("");
    setLinkAmount("");
    setLinkFee("");
    setLinkTotal("");
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!amount || parseFloat(amount) <= 0) return toast.error("Inserisci un importo valido");
    if (!description.trim()) return toast.error("Inserisci una descrizione");

    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/payment/create-invoice`, {
        password,
        base_amount_eur: parseFloat(amount),
        description,
        customer_name: customerName || null,
        customer_phone: customerPhone || null,
        customer_email: customerEmail || null,
        fee_percent_override: parseFloat(feePercent),
      });
      setLink(data.branded_url);
      setTapLink(data.hosted_url || "");
      setClientRef(data.client_ref);
      setLinkAmount(data.base_amount);
      setLinkFee(data.service_fee);
      setLinkTotal(data.total_amount);
      setLinkType("payment");
      setLinkLang("en");
      if (!data.provider_configured) {
        toast.warning("Stripe non configurato — link brandizzato generato, checkout esterno non disponibile");
      } else {
        toast.success("Link di pagamento generato");
      }
      fetchHistory(password);
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      if (err?.response?.status === 401) {
        sessionStorage.removeItem("lr_admin_pw");
        setAuthed(false);
        toast.error("Password non valida");
      } else {
        toast.error(String(detail).slice(0, 200));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success("Link copiato");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Impossibile copiare");
    }
  };

  const handleSendWhatsApp = () => {
    const phoneDigits = customerPhone.replace(/[^0-9]/g, "");
    const w = waT(linkLang);
    const msg = [
      `${w.greeting}${customerName ? " " + customerName : ""},`,
      "",
      w.linkIntro,
      "",
      `${description}`,
      `${w.productAmount}: €${linkAmount}`,
      `${w.fee} ${feePreview.service_fee_percent}%: €${linkFee}`,
      `${w.total}: €${linkTotal}`,
      "",
      w.payHere,
      link,
      "",
      w.thanks,
    ].join("\n");
    const url = `https://wa.me/${phoneDigits}?text=${encodeURIComponent(msg)}`;
    window.open(url, "_blank");
  };

  const handleSendReceiptWhatsApp = () => {
    if (!clientRef) return;
    const phoneDigits = customerPhone.replace(/[^0-9]/g, "");
    const w = waT(linkLang);
    const receiptUrl = `${BACKEND}/api/payment/receipt/${clientRef}.pdf`;
    const msg = [
      `${w.greeting}${customerName ? " " + customerName : ""},`,
      "",
      w.receiptIntro,
      "",
      `${description}`,
      `${w.total}: €${linkTotal}`,
      "",
      w.receiptDownload,
      receiptUrl,
      "",
      w.receiptThanks,
    ].join("\n");
    const url = `https://wa.me/${phoneDigits}?text=${encodeURIComponent(msg)}`;
    window.open(url, "_blank");
  };

  const statusColor = (s) => {
    if (s === "PAID" || s === "CAPTURED") return { bg: "bg-[#009A49]/20", text: "text-[#009A49]", border: "border-[#009A49]/40" };
    if (s === "FAILED" || s === "DECLINED" || s === "CANCELLED") return { bg: "bg-red-500/20", text: "text-red-400", border: "border-red-500/40" };
    return { bg: "bg-amber-500/10", text: "text-amber-400", border: "border-amber-500/30" };
  };

  const statusLabel = (s) => {
    if (s === "PAID" || s === "CAPTURED") return "Pagato";
    if (s === "FAILED" || s === "DECLINED") return "Fallito";
    if (s === "CANCELLED") return "Annullato";
    if (s === "EXPIRED") return "Scaduto";
    return "In attesa";
  };

  // Order status (WhatsApp archive)
  const orderStatusMeta = (s) => {
    if (s === "PAID") return { label: "Pagato", bg: "bg-[#009A49]/20", text: "text-[#009A49]", border: "border-[#009A49]/40" };
    if (s === "LINK_SENT") return { label: "Link Inviato", bg: "bg-blue-500/15", text: "text-blue-400", border: "border-blue-500/40" };
    if (s === "SHIPPED") return { label: "Spedito", bg: "bg-violet-500/15", text: "text-violet-400", border: "border-violet-500/40" };
    if (s === "FAILED") return { label: "Fallito", bg: "bg-red-500/20", text: "text-red-400", border: "border-red-500/40" };
    return { label: "In Attesa", bg: "bg-amber-500/10", text: "text-amber-400", border: "border-amber-500/30" };
  };

  const handleGenerateForOrder = async (order, linkTypeReq = "payment") => {
    setGeneratingFor(`${order.id}:${linkTypeReq}`);
    try {
      const { data } = await axios.post(`${API}/orders/generate-payment`, {
        password,
        order_id: order.id,
        fee_percent_override: parseFloat(feePercent),
        link_type: linkTypeReq,
      });
      setLink(data.branded_url);
      setLinkType(data.link_type || linkTypeReq);
      setLinkLang(order.lang || "en");
      setTapLink(data.hosted_url || "");
      setClientRef(data.client_ref);
      setLinkAmount(data.base_amount);
      setLinkFee(data.service_fee);
      setLinkTotal(data.total_amount);
      // Precompila i campi cliente così Invia/Ricevuta WhatsApp funzionano subito
      setCustomerName(order.customer_name || "");
      setCustomerPhone(order.customer_phone || "");
      setCustomerEmail(order.customer_email || "");
      setAmount(String(data.base_amount));
      const desc = (order.items || []).map((it) => `${it.quantity}x ${it.jersey} (${it.category_label})`).join(" · ");
      setDescription(desc.slice(0, 200));
      if (data.link_type === "shipping_only") {
        toast.success("Link solo spedizione generato — il cliente inserirà solo l'indirizzo");
      } else if (!data.provider_configured) {
        toast.warning("Stripe non configurato — link brandizzato generato, checkout esterno non disponibile");
      } else {
        toast.success("Link generato dall'ordine");
      }
      fetchOrders(password);
      fetchHistory(password);
      // scroll to the generated link result
      setTimeout(() => {
        formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      if (err?.response?.status === 401) {
        sessionStorage.removeItem("lr_admin_pw");
        setAuthed(false);
        toast.error("Password non valida");
      } else {
        toast.error(String(detail).slice(0, 200));
      }
    } finally {
      setGeneratingFor(null);
    }
  };

  const totalFeeForOrder = (base, orderFeePct) => {
    const pct = orderFeePct !== undefined && orderFeePct !== null ? orderFeePct : (parseFloat(feePercent) || 3.5);
    const fee = Math.round(base * pct) / 100;
    return { fee: fee.toFixed(2), total: (base + fee).toFixed(2), pct };
  };

  const handleDeleteOrder = async (order) => {
    if (!window.confirm(`Eliminare definitivamente l'ordine di ${order.customer_name}?`)) return;
    setDeletingOrder(order.id);
    try {
      await axios.post(`${API}/orders/${order.id}/delete`, { password });
      toast.success("Ordine eliminato");
      fetchOrders(password);
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      toast.error(String(detail).slice(0, 200));
    } finally {
      setDeletingOrder(null);
    }
  };

  const handleSaveEditedOrder = async (edited) => {
    try {
      const payload = {
        password,
        customer_name: edited.customer_name,
        customer_email: edited.customer_email,
        customer_phone: edited.customer_phone,
        country: edited.country,
        state: edited.state,
        city: edited.city,
        address: edited.address,
        zip: edited.zip,
        notes: edited.notes,
        items: edited.items.map((it) => ({
          category: it.category,
          jersey: it.jersey,
          size: it.size,
          quantity: parseInt(it.quantity, 10) || 1,
          personalization: it.personalization || null,
          patch: it.patch || "none",
          patch_label: it.patch_label || null,
        })),
      };
      await axios.post(`${API}/orders/${edited.id}/update`, payload);
      toast.success("Ordine aggiornato");
      setEditingOrder(null);
      fetchOrders(password);
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      toast.error(String(detail).slice(0, 200));
    }
  };

  const handleQuickUpdateOrder = async (order, patch) => {
    try {
      await axios.post(`${API}/orders/${order.id}/update`, { password, ...patch });
      fetchOrders(password);
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      toast.error(String(detail).slice(0, 200));
    }
  };

  const handleSendOrderReceiptWhatsApp = (order) => {
    const phone = String(order.customer_phone || "").replace(/[^\d]/g, "");
    const w = waT(order.lang);
    if (!phone) {
      toast.error("Numero WhatsApp cliente mancante");
      return;
    }
    const fee_pct = parseFloat(feePercent) || 3.5;
    const base = Number(order.base_amount || 0);
    const total = base + Math.round(base * fee_pct) / 100;
    const paidAt = order.paid_at || order.updated_at || order.created_at;
    let dateStr = "";
    try {
      dateStr = new Date(paidAt).toLocaleString("it-IT", { day: "2-digit", month: "long", year: "numeric" });
    } catch { dateStr = paidAt || ""; }
    const itemsLine = (order.items || [])
      .map((it) => `• ${it.quantity}x ${it.jersey} (${it.category_label}, ${w.sizeWord} ${it.size})`)
      .join("\n");
    const pmLabels = { stripe: "Stripe", wise: "Wise", cash: w.pmCash, other: w.pmOther };
    const pmDisplay = pmLabels[order.payment_method || "stripe"] || "Stripe";
    const receiptUrl = `${API}/orders/${order.id}/receipt.pdf`;
    const msg =
`${w.greeting} ${order.customer_name || ""}, ${w.receiptIntro}

${itemsLine}

${w.amountPaid}: €${total.toFixed(2)}
${w.method}: ${pmDisplay}
${w.date}: ${dateStr}

${w.receiptDownload}
${receiptUrl}

${w.receiptFooter}`;
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
    window.open(url, "_blank");
  };

  const handleDeleteInvoice = async (inv) => {
    if (!window.confirm(`Eliminare il pagamento ${inv.client_ref} di ${inv.customer_name || "-"}? L'ordine collegato tornerà in stato "In Attesa".`)) return;
    setDeletingInvoice(inv.client_ref);
    try {
      await axios.post(`${API}/payment/invoice/${inv.client_ref}/delete`, { password });
      toast.success("Pagamento eliminato");
      fetchHistory(password);
      fetchOrders(password);
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      toast.error(String(detail).slice(0, 200));
    } finally {
      setDeletingInvoice(null);
    }
  };

  const handleSaveEditedInvoice = async (edited) => {
    try {
      await axios.post(`${API}/payment/invoice/${edited.client_ref}/update`, {
        password,
        status: edited.status,
        customer_name: edited.customer_name,
        customer_email: edited.customer_email,
        customer_phone: edited.customer_phone,
        description: edited.description,
      });
      toast.success("Pagamento aggiornato");
      setEditingInvoice(null);
      fetchHistory(password);
      fetchOrders(password);
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      toast.error(String(detail).slice(0, 200));
    }
  };

  // -------- PASSWORD GATE --------
  if (!authed) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="w-full max-w-md">
          <div className="text-center mb-10">
            <div className="font-heading text-3xl md:text-4xl font-bold tracking-[0.15em] mb-3">
              <span className="text-[#009A49]">LOCKER</span>{" "}
              <span className="text-white">ROOM</span>
            </div>
            <img src="/lr-monogram.svg" alt="Locker Room – boutique premium di maglie sportive" className="h-16 w-auto mx-auto mb-4" />
            <p className="text-zinc-500 font-body text-xs tracking-[0.3em] uppercase">Admin · Pagamenti</p>
          </div>

          <form onSubmit={handleUnlock} className="bg-[#0A0A0A] border border-white/10 rounded-sm p-8" data-testid="pw-form">
            <div className="flex items-center gap-3 mb-6">
              <Lock className="w-5 h-5 text-[#009A49]" />
              <h1 className="font-heading text-xl font-bold text-white">Area Protetta</h1>
            </div>
            <label className="block text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 mb-3 font-body">Password</label>
            <input
              type="password"
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full px-4 py-3 mb-4 bg-transparent border border-white/10 rounded-sm text-white font-body focus:outline-none focus:border-[#009A49] transition-colors"
              data-testid="pw-input"
            />
            {pwError && <p className="text-red-500 text-sm mb-4" data-testid="pw-error">{pwError}</p>}
            <button
              type="submit"
              disabled={logging}
              className="w-full bg-[#009A49] hover:bg-[#00b356] disabled:opacity-50 text-white font-body font-bold text-sm tracking-[0.2em] uppercase py-4 rounded-sm transition-colors flex items-center justify-center gap-3"
              data-testid="pw-submit-btn"
            >
              {logging ? <><Loader2 className="w-4 h-4 animate-spin" /> Accesso...</> : "Accedi"}
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  // -------- ADMIN CONSOLE --------
  return (
    <div className="min-h-screen bg-black py-10 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-10">
          <div className="flex items-center gap-4">
            <img src="/lr-monogram.svg" alt="Locker Room – boutique premium di maglie sportive" className="h-14 w-auto" />
            <div>
              <div className="font-heading text-2xl font-bold tracking-[0.1em]">
                <span className="text-[#009A49]">LOCKER</span>{" "}
                <span className="text-white">ROOM</span>
              </div>
              <p className="text-zinc-500 font-body text-[10px] tracking-[0.3em] uppercase">Admin · Genera Link Pagamento</p>
            </div>
          </div>
          <button onClick={handleLogout} className="text-zinc-400 hover:text-white flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-body" data-testid="logout-btn">
            <LogOut className="w-4 h-4" />
            Esci
          </button>
        </div>

        {/* Orders Archive (WhatsApp) */}
        <div className="mb-12" data-testid="orders-archive">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Package className="w-5 h-5 text-[#009A49]" />
              <h2 className="font-heading text-2xl font-bold text-white">Archivio Ordini</h2>
              {orders.length > 0 && (
                <span className="text-xs font-body text-zinc-500 tracking-wider" data-testid="orders-count">
                  {orders.length} totali
                </span>
              )}
            </div>
            <button
              onClick={() => fetchOrders(password)}
              className="text-zinc-400 hover:text-white text-xs tracking-[0.2em] uppercase font-body flex items-center gap-2"
              data-testid="refresh-orders-btn"
            >
              <RefreshCw className={`w-4 h-4 ${ordersLoading ? "animate-spin" : ""}`} />
              Aggiorna
            </button>
          </div>

          {orders.length > 0 && (
            <div className="mb-4 space-y-3">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none" />
                <input
                  type="text"
                  value={ordersQuery}
                  onChange={(e) => setOrdersQuery(e.target.value)}
                  placeholder="Cerca per nome, telefono, città o articolo..."
                  className="w-full pl-11 pr-4 py-3 bg-[#0A0A0A] border border-white/10 rounded-sm text-white font-body text-sm focus:outline-none focus:border-[#009A49] transition-colors placeholder:text-zinc-600"
                  data-testid="orders-search-input"
                />
              </div>
              <div className="flex flex-wrap items-center gap-2" data-testid="orders-status-filter">
                {[
                  { key: "all", label: "Tutti" },
                  { key: "PENDING", label: "In Attesa" },
                  { key: "LINK_SENT", label: "Link Inviato" },
                  { key: "PAID", label: "Pagato" },
                  { key: "SHIPPED", label: "Spedito" },
                ].map((f) => {
                  const active = ordersStatusFilter === f.key;
                  const activeClasses = {
                    all: "bg-white text-black border-white",
                    PENDING: "bg-amber-500 text-black border-amber-500",
                    LINK_SENT: "bg-blue-500 text-white border-blue-500",
                    PAID: "bg-[#009A49] text-white border-[#009A49]",
                    SHIPPED: "bg-violet-500 text-white border-violet-500",
                  }[f.key];
                  return (
                    <button
                      key={f.key}
                      onClick={() => setOrdersStatusFilter(f.key)}
                      className={`px-4 py-1.5 rounded-full border text-[11px] font-body font-semibold tracking-[0.15em] uppercase transition-colors ${
                        active ? activeClasses : "bg-transparent border-white/10 text-zinc-400 hover:border-white/30 hover:text-white"
                      }`}
                      data-testid={`orders-filter-${f.key}`}
                    >
                      {f.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {orders.length === 0 ? (
            <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-8 text-center" data-testid="orders-empty">
              <Package className="w-8 h-8 text-zinc-600 mx-auto mb-3" />
              <p className="text-zinc-500 font-body text-sm">Nessun ordine ricevuto</p>
              <p className="text-zinc-600 font-body text-xs mt-1">Gli ordini inviati dai clienti via WhatsApp appariranno qui automaticamente.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {(() => {
                const q = ordersQuery.trim().toLowerCase();
                let rows = orders;
                if (ordersStatusFilter !== "all") {
                  rows = rows.filter((o) => (o.status || "PENDING") === ordersStatusFilter);
                }
                if (q) {
                  rows = rows.filter((o) =>
                    (o.customer_name || "").toLowerCase().includes(q) ||
                    (o.customer_phone || "").toLowerCase().includes(q) ||
                    (o.customer_email || "").toLowerCase().includes(q) ||
                    (o.city || "").toLowerCase().includes(q) ||
                    (o.items || []).some((it) => (it.jersey || "").toLowerCase().includes(q))
                  );
                }
                if (rows.length === 0) {
                  return (
                    <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-6 text-center text-zinc-500 text-sm" data-testid="orders-empty-search">
                      Nessun ordine trovato con i filtri applicati.
                    </div>
                  );
                }
                return rows.map((order) => {
                  const meta = orderStatusMeta(order.status || "PENDING");
                  const expanded = expandedOrder === order.id;
                  const dt = order.created_at
                    ? new Date(order.created_at).toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
                    : "-";
                  const feeInfo = totalFeeForOrder(Number(order.base_amount || 0), order.service_fee_percent);
                  const isGenerating = generatingFor === `${order.id}:payment`;
                  const isGeneratingShip = generatingFor === `${order.id}:shipping_only`;
                  return (
                    <div
                      key={order.id}
                      className="bg-[#0A0A0A] border border-white/10 rounded-sm overflow-hidden"
                      data-testid={`order-row-${order.id}`}
                    >
                      <div className="flex flex-col md:flex-row md:items-center gap-4 p-4 md:p-5">
                        <button
                          onClick={() => setExpandedOrder(expanded ? null : order.id)}
                          className="flex-1 text-left"
                          data-testid={`order-toggle-${order.id}`}
                        >
                          <div className="flex items-center gap-3 mb-1">
                            <span className={`inline-block px-2.5 py-0.5 rounded-full text-[10px] tracking-[0.2em] uppercase font-bold border ${meta.bg} ${meta.text} ${meta.border}`}>
                              {meta.label}
                            </span>
                            <span className="text-zinc-500 font-body text-xs">{dt}</span>
                          </div>
                          <div className="flex items-baseline gap-3 flex-wrap">
                            <span className="font-heading text-white text-lg tracking-wide">{order.customer_name || "—"}</span>
                            <span className="text-zinc-500 font-body text-xs">{order.customer_phone}</span>
                            <span className="text-zinc-600 font-body text-xs">· {order.total_quantity} art. · €{Number(order.base_amount || 0).toFixed(2)}</span>
                            {order.has_discount && (
                              <span className="px-2 py-0.5 rounded-full text-[10px] tracking-wider uppercase font-bold bg-[#009A49]/20 text-[#009A49] border border-[#009A49]/40" data-testid={`order-discount-badge-${order.id}`}>
                                −{Number(order.discount_percent ?? 10).toFixed(0)}%
                              </span>
                            )}
                          </div>
                        </button>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {!["PAID", "SHIPPED"].includes(order.status || "PENDING") && (order.payment_method || "stripe") === "stripe" && (
                            <button
                              onClick={() => handleGenerateForOrder(order, "payment")}
                              disabled={isGenerating || isGeneratingShip}
                              className="bg-[#009A49] hover:bg-[#00b356] disabled:opacity-50 disabled:cursor-not-allowed text-white font-body font-semibold text-[11px] tracking-[0.15em] uppercase px-4 py-2.5 rounded-sm transition-colors flex items-center gap-2"
                              data-testid={`order-generate-btn-${order.id}`}
                            >
                              {isGenerating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
                              {order.status === "LINK_SENT" ? "Rigenera Link Pagamento" : "Link Pagamento"}
                            </button>
                          )}
                          {!["PAID", "SHIPPED"].includes(order.status || "PENDING") && (
                            <button
                              onClick={() => handleGenerateForOrder(order, "shipping_only")}
                              disabled={isGenerating || isGeneratingShip}
                              className="border border-[#009A49]/40 text-[#009A49] hover:bg-[#009A49]/10 disabled:opacity-50 disabled:cursor-not-allowed font-body font-semibold text-[11px] tracking-[0.15em] uppercase px-4 py-2.5 rounded-sm transition-colors flex items-center gap-2"
                              data-testid={`order-shipping-link-btn-${order.id}`}
                              title="Il cliente inserisce solo i dati di spedizione — nessun pagamento online"
                            >
                              {isGeneratingShip ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Truck className="w-3.5 h-3.5" />}
                              Link Solo Spedizione
                            </button>
                          )}
                          {!["PAID", "SHIPPED"].includes(order.status || "PENDING") && (order.payment_method || "stripe") !== "stripe" && (
                            <button
                              onClick={() => handleQuickUpdateOrder(order, { status: "PAID" })}
                              className="bg-[#009A49] hover:bg-[#00b356] text-white font-body font-semibold text-[11px] tracking-[0.15em] uppercase px-4 py-2.5 rounded-sm transition-colors flex items-center gap-2"
                              data-testid={`order-mark-paid-btn-${order.id}`}
                              title={`Segna come pagato (${(order.payment_method || "").toUpperCase()})`}
                            >
                              <Check className="w-3.5 h-3.5" />
                              Segna come Pagato
                            </button>
                          )}
                          <button
                            onClick={() => setEditingOrder(order)}
                            className="text-zinc-400 hover:text-white border border-white/10 hover:border-white/30 p-2 rounded-sm transition-colors"
                            aria-label="Modifica ordine"
                            data-testid={`order-edit-btn-${order.id}`}
                            title="Modifica ordine"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => handleDeleteOrder(order)}
                            disabled={deletingOrder === order.id}
                            className="text-red-400 hover:text-red-300 border border-red-500/20 hover:border-red-500/50 p-2 rounded-sm transition-colors disabled:opacity-50"
                            aria-label="Elimina ordine"
                            data-testid={`order-delete-btn-${order.id}`}
                            title="Elimina ordine"
                          >
                            {deletingOrder === order.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                          </button>
                          <button
                            onClick={() => setExpandedOrder(expanded ? null : order.id)}
                            className="text-zinc-500 hover:text-white p-2"
                            aria-label="Espandi"
                          >
                            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>

                      {expanded && (
                        <div className="border-t border-white/5 bg-black/40 p-4 md:p-5 space-y-4" data-testid={`order-details-${order.id}`}>
                          {/* Quick Status + Payment Method + Discount + Fee controls */}
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            <div>
                              <p className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 font-body mb-1.5">Stato pagamento</p>
                              <select
                                value={order.status || "PENDING"}
                                onChange={(e) => handleQuickUpdateOrder(order, { status: e.target.value })}
                                className="edit-input w-full"
                                data-testid={`order-status-select-${order.id}`}
                              >
                                <option value="PENDING" className="bg-black">In Attesa</option>
                                <option value="LINK_SENT" className="bg-black">Link Inviato</option>
                                <option value="PAID" className="bg-black">Pagato</option>
                                <option value="SHIPPED" className="bg-black">Spedito</option>
                                <option value="CANCELLED" className="bg-black">Annullato</option>
                                <option value="FAILED" className="bg-black">Fallito</option>
                              </select>
                            </div>
                            <div>
                              <p className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 font-body mb-1.5">Metodo di pagamento</p>
                              <select
                                value={order.payment_method || "stripe"}
                                onChange={(e) => handleQuickUpdateOrder(order, { payment_method: e.target.value })}
                                className="edit-input w-full"
                                data-testid={`order-payment-method-${order.id}`}
                              >
                                <option value="stripe" className="bg-black">Stripe</option>
                                <option value="wise" className="bg-black">Wise</option>
                                <option value="cash" className="bg-black">Contanti</option>
                                <option value="other" className="bg-black">Altro</option>
                              </select>
                            </div>
                            <div>
                              <p className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 font-body mb-1.5">Sconto %</p>
                              <DiscountInput
                                value={order.discount_percent !== undefined && order.discount_percent !== null ? order.discount_percent : (order.has_discount ? 10 : 0)}
                                onCommit={(pct) => handleQuickUpdateOrder(order, { discount_percent: pct })}
                                testIdBase={`order-discount-${order.id}`}
                              />
                            </div>
                            <div>
                              <p className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 font-body mb-1.5">Fee % <span className="normal-case text-zinc-600 tracking-normal">· service fee</span></p>
                              <DiscountInput
                                value={order.service_fee_percent !== undefined && order.service_fee_percent !== null ? order.service_fee_percent : 3.5}
                                onCommit={(pct) => handleQuickUpdateOrder(order, { service_fee_percent: pct })}
                                testIdBase={`order-fee-${order.id}`}
                                type="fee"
                              />
                            </div>
                          </div>

                          {/* Send Receipt via WhatsApp (only when PAID or SHIPPED) */}
                          {(order.status === "PAID" || order.status === "SHIPPED") && (
                            <button
                              onClick={() => handleSendOrderReceiptWhatsApp(order)}
                              className="w-full bg-[#25D366] hover:bg-[#128C7E] text-white font-body font-semibold text-xs tracking-[0.15em] uppercase px-4 py-3 rounded-sm transition-colors flex items-center justify-center gap-2"
                              data-testid={`order-send-receipt-${order.id}`}
                            >
                              <MessageCircle className="w-4 h-4" />
                              Invia Ricevuta WhatsApp
                            </button>
                          )}

                          {/* Shipping */}
                          <div>
                            <p className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 font-body mb-2">Spedizione</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm text-zinc-300 font-body">
                              <div><span className="text-zinc-500">Destinatario:</span> {order.shipping_name || order.customer_name || "—"}</div>
                              <div><span className="text-zinc-500">Email:</span> {order.customer_email || "—"}</div>
                              <div><span className="text-zinc-500">Telefono:</span> {order.customer_phone || "—"}</div>
                              <div><span className="text-zinc-500">Indirizzo:</span> {order.address || "—"}</div>
                              <div><span className="text-zinc-500">Città:</span> {order.city || "—"}</div>
                              <div><span className="text-zinc-500">Provincia:</span> {order.state || "—"}</div>
                              <div><span className="text-zinc-500">CAP:</span> {order.zip || "—"}</div>
                              <div><span className="text-zinc-500">Paese:</span> {order.country || "—"}</div>
                            </div>
                          </div>

                          {/* Items */}
                          <div>
                            <p className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 font-body mb-2">Articoli</p>
                            <div className="space-y-1.5">
                              {(order.items || []).map((it, i) => (
                                <div key={i} className="flex items-start justify-between gap-3 text-sm font-body">
                                  <div className="flex-1">
                                    <p className="text-white">{it.jersey} <span className="text-zinc-500 text-xs">· {it.category_label}</span></p>
                                    <p className="text-zinc-500 text-xs">
                                      Taglia {it.size || "—"} · x{it.quantity}
                                      {it.personalization ? ` · ${it.personalization}` : ""}
                                      {it.patch_label ? ` · Patch: ${it.patch_label}` : ""}
                                      {it.notes ? ` · Note: ${it.notes}` : ""}
                                    </p>
                                  </div>
                                  <span className="text-zinc-300 font-mono text-xs whitespace-nowrap">€{Number(it.unit_price).toFixed(2)} × {it.quantity} = €{Number(it.line_total).toFixed(2)}</span>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Notes */}
                          {order.notes && (
                            <div>
                              <p className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 font-body mb-2">Note</p>
                              <p className="text-sm text-zinc-300 font-body italic">&ldquo;{order.notes}&rdquo;</p>
                            </div>
                          )}

                          {/* Amount summary + reference */}
                          {order.has_discount && (
                            <div className="flex justify-between items-center text-xs text-zinc-400 font-body px-1 pt-1">
                              <span>Subtotale articoli</span>
                              <span className="font-mono">€{Number(order.gross_amount || 0).toFixed(2)}</span>
                            </div>
                          )}
                          {order.has_discount && (
                            <div className="flex justify-between items-center text-xs text-[#009A49] font-body px-1">
                              <span>Sconto multi-articolo (−{Number(order.discount_percent ?? 10).toFixed(0)}%)</span>
                              <span className="font-mono">−€{Number(order.discount_amount || 0).toFixed(2)}</span>
                            </div>
                          )}
                          <div className="grid grid-cols-3 gap-2 pt-2">
                            <div className="bg-black/40 border border-white/5 rounded-sm p-3">
                              <p className="text-zinc-500 tracking-wider uppercase text-[10px] mb-1">{order.has_discount ? "Prodotti (scontato)" : "Prodotti"}</p>
                              <p className="font-mono text-white text-sm">€{Number(order.base_amount || 0).toFixed(2)}</p>
                            </div>
                            <div className="bg-black/40 border border-white/5 rounded-sm p-3">
                              <p className="text-zinc-500 tracking-wider uppercase text-[10px] mb-1">Fee {feeInfo.pct}%</p>
                              <p className="font-mono text-white text-sm">€{feeInfo.fee}</p>
                            </div>
                            <div className="bg-[#009A49]/20 border border-[#009A49]/40 rounded-sm p-3">
                              <p className="text-[#009A49] tracking-wider uppercase text-[10px] mb-1">Totale</p>
                              <p className="font-mono text-[#009A49] font-bold text-sm">€{feeInfo.total}</p>
                            </div>
                          </div>

                          {order.invoice_client_ref && (
                            <div className="text-xs text-zinc-500 font-body pt-1">
                              Link fattura: <span className="text-[#009A49] font-mono">{order.invoice_client_ref}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                });
              })()}
            </div>
          )}
        </div>

        {/* Form */}
        <motion.form ref={formRef} onSubmit={handleGenerate} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="bg-[#0A0A0A] border border-white/10 rounded-sm p-6 md:p-8 space-y-5" data-testid="payment-form">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-2">Nuovo Pagamento</h2>
          <p className="text-zinc-400 text-sm mb-6">Compila l&apos;importo, condividi il link col cliente. Il cliente potrà pagare con carta, Apple Pay o Google Pay.</p>

          <div>
            <label className="block text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 mb-3 font-body">Importo base (EUR) *</label>
            <div className="relative">
              <Euro className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#009A49]" />
              <input type="number" min="0.01" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" required
                className="w-full pl-12 pr-4 py-4 bg-transparent border border-white/10 rounded-sm text-white text-2xl font-body focus:outline-none focus:border-[#009A49] transition-colors" data-testid="amount-input" />
            </div>
            {/* Live fee preview */}
            {feePreview.total_amount > 0 && (
              <div className="mt-3 p-4 bg-black/40 border border-white/5 rounded-sm space-y-2 text-sm" data-testid="fee-preview">
                <div className="flex justify-between text-zinc-400 font-body"><span>Importo prodotto</span><span className="font-mono">€{amount ? parseFloat(amount).toFixed(2) : "0.00"}</span></div>
                <div className="flex justify-between text-zinc-400 font-body"><span>Service Fee {feePreview.service_fee_percent}%</span><span className="font-mono">€{feePreview.service_fee.toFixed(2)}</span></div>
                <div className="flex justify-between pt-2 border-t border-white/10 text-white font-body font-semibold"><span>Totale da pagare</span><span className="font-mono text-[#009A49] text-base">€{feePreview.total_amount.toFixed(2)}</span></div>
              </div>
            )}
          </div>

          {/* Fee selector */}
          <div>
            <label className="block text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 mb-3 font-body">Service Fee %</label>
            <div className="grid grid-cols-5 gap-2" data-testid="fee-selector">
              {["0", "2", "3.5", "5"].map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => { setCustomFee(false); setFeePercent(v); }}
                  className={`py-3 rounded-sm border transition-colors text-sm font-body font-semibold ${
                    !customFee && feePercent === v
                      ? "bg-[#009A49] border-[#009A49] text-white"
                      : "bg-transparent border-white/10 text-zinc-400 hover:border-white/30 hover:text-white"
                  }`}
                  data-testid={`fee-option-${v}`}
                >
                  {v}%
                </button>
              ))}
              <button
                type="button"
                onClick={() => setCustomFee(true)}
                className={`py-3 rounded-sm border transition-colors text-sm font-body font-semibold ${
                  customFee
                    ? "bg-[#009A49] border-[#009A49] text-white"
                    : "bg-transparent border-white/10 text-zinc-400 hover:border-white/30 hover:text-white"
                }`}
                data-testid="fee-option-custom"
              >
                Custom
              </button>
            </div>
            {customFee && (
              <div className="mt-3 flex items-center gap-2" data-testid="fee-custom-input-wrap">
                <input
                  type="number"
                  min="0"
                  max="20"
                  step="0.1"
                  value={feePercent}
                  onChange={(e) => setFeePercent(e.target.value)}
                  placeholder="es. 4.2"
                  autoFocus
                  className="flex-1 px-4 py-3 bg-transparent border border-white/10 rounded-sm text-white font-body focus:outline-none focus:border-[#009A49] transition-colors placeholder:text-zinc-600"
                  data-testid="fee-custom-input"
                />
                <span className="text-white font-body font-semibold">%</span>
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 mb-3 font-body">Descrizione *</label>
            <input type="text" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="es. Maglia PSG Home 26/27" required maxLength={200}
              className="w-full px-4 py-3 bg-transparent border border-white/10 rounded-sm text-white font-body focus:outline-none focus:border-[#009A49] transition-colors placeholder:text-zinc-600" data-testid="description-input" />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 mb-3 font-body">Nome cliente</label>
              <input type="text" value={customerName} onChange={(e) => setCustomerName(e.target.value)} placeholder="Mario Rossi"
                className="w-full px-4 py-3 bg-transparent border border-white/10 rounded-sm text-white font-body focus:outline-none focus:border-[#009A49] transition-colors placeholder:text-zinc-600" data-testid="customer-name-input" />
            </div>
            <div>
              <label className="block text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 mb-3 font-body">WhatsApp cliente</label>
              <input type="tel" value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} placeholder="+39 320 000 0000"
                className="w-full px-4 py-3 bg-transparent border border-white/10 rounded-sm text-white font-body focus:outline-none focus:border-[#009A49] transition-colors placeholder:text-zinc-600" data-testid="customer-phone-input" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 mb-3 font-body">Email cliente (opzionale)</label>
            <input type="email" value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} placeholder="cliente@email.com"
              className="w-full px-4 py-3 bg-transparent border border-white/10 rounded-sm text-white font-body focus:outline-none focus:border-[#009A49] transition-colors placeholder:text-zinc-600" data-testid="customer-email-input" />
          </div>

          <button type="submit" disabled={submitting} className="w-full bg-[#009A49] hover:bg-[#00b356] disabled:opacity-50 disabled:cursor-not-allowed text-white font-body font-bold text-sm tracking-[0.2em] uppercase py-4 rounded-full transition-colors flex items-center justify-center gap-3" data-testid="generate-link-btn">
            {submitting ? <><Loader2 className="w-5 h-5 animate-spin" /> Generazione...</> : <><Send className="w-4 h-4" /> Genera Link di Pagamento</>}
          </button>
        </motion.form>

        {/* Result */}
        {link && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-8 bg-[#0A0A0A] border-2 border-[#009A49] rounded-sm p-6 md:p-8" data-testid="payment-link-result">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-full bg-[#009A49] flex items-center justify-center">
                <Check className="w-5 h-5 text-white" />
              </div>
              <h3 className="font-heading text-xl font-bold text-white">Link Pronto</h3>
              {linkType === "shipping_only" && (
                <span className="px-2.5 py-1 rounded-full text-[10px] tracking-[0.15em] uppercase font-bold border bg-cyan-500/15 text-cyan-300 border-cyan-500/40" data-testid="link-type-badge">Solo Spedizione</span>
              )}
            </div>
            <p className="text-zinc-400 text-sm mb-2">Ordine <span className="text-[#009A49] font-mono">{clientRef}</span></p>
            <div className="grid grid-cols-3 gap-2 mb-4 text-xs">
              <div className="bg-black/40 border border-white/5 rounded-sm p-3">
                <p className="text-zinc-500 tracking-wider uppercase text-[10px] mb-1">Prodotto</p>
                <p className="font-mono text-white">€{linkAmount}</p>
              </div>
              <div className="bg-black/40 border border-white/5 rounded-sm p-3">
                <p className="text-zinc-500 tracking-wider uppercase text-[10px] mb-1">Service Fee</p>
                <p className="font-mono text-white">€{linkFee}</p>
              </div>
              <div className="bg-[#009A49]/20 border border-[#009A49]/40 rounded-sm p-3">
                <p className="text-[#009A49] tracking-wider uppercase text-[10px] mb-1">Totale</p>
                <p className="font-mono text-[#009A49] font-bold">€{linkTotal}</p>
              </div>
            </div>
            <div className="bg-black border border-white/10 rounded-sm p-4 mb-4 break-all font-mono text-xs text-zinc-300" data-testid="link-display">{link}</div>
            {linkType === "shipping_only" && (
              <div className="mb-4 p-3 rounded-sm bg-cyan-500/10 border border-cyan-500/30 text-cyan-300 text-xs font-body" data-testid="shipping-only-note">
                Link solo spedizione: il cliente vedrà solo il modulo per l'indirizzo — nessun pagamento online. Gestirai il pagamento separatamente (contanti, Wise, ecc.).
              </div>
            )}
            {linkType === "payment" && !tapLink && (
              <div className="mb-4 p-3 rounded-sm bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs font-body">
                Stripe non ancora configurato — il link mostra la pagina brandizzata ma il checkout esterno non è disponibile finché non aggiungi le credenziali reali nei Secrets.
              </div>
            )}
            <div className="flex flex-col sm:flex-row gap-3">
              <button onClick={handleCopy} className="flex-1 bg-white/5 hover:bg-white/10 text-white font-body font-semibold text-xs tracking-[0.2em] uppercase py-3 rounded-sm transition-colors border border-white/10 flex items-center justify-center gap-2" data-testid="copy-link-btn">
                {copied ? <Check className="w-4 h-4 text-[#009A49]" /> : <Copy className="w-4 h-4" />}
                {copied ? "Copiato" : "Copia Link"}
              </button>
              <button onClick={handleSendWhatsApp} className="flex-1 bg-[#25D366] hover:bg-[#20b757] text-white font-body font-bold text-xs tracking-[0.2em] uppercase py-3 rounded-sm transition-colors flex items-center justify-center gap-2" data-testid="send-whatsapp-btn">
                <MessageCircle className="w-4 h-4" />
                Invia via WhatsApp
              </button>
              <a
                href={`${API}/payment/receipt/${clientRef}.pdf`}
                target="_blank"
                rel="noreferrer"
                className="flex-1 bg-white/5 hover:bg-white/10 text-white font-body font-semibold text-xs tracking-[0.2em] uppercase py-3 rounded-sm transition-colors border border-white/10 flex items-center justify-center gap-2"
                data-testid="download-receipt-btn"
              >
                <FileText className="w-4 h-4" />
                Ricevuta PDF
              </a>
            </div>
            {/* Second row: send receipt via WhatsApp */}
            <button
              onClick={handleSendReceiptWhatsApp}
              className="w-full mt-3 bg-white/5 hover:bg-white/10 text-[#25D366] hover:text-white font-body font-bold text-xs tracking-[0.2em] uppercase py-3 rounded-sm transition-colors border border-[#25D366]/30 hover:border-[#25D366] flex items-center justify-center gap-2"
              data-testid="send-receipt-whatsapp-btn"
            >
              <FileText className="w-4 h-4" />
              Invia Ricevuta PDF su WhatsApp
            </button>
            <button onClick={resetForm} className="w-full mt-4 text-zinc-500 hover:text-white text-xs tracking-[0.2em] uppercase font-body py-2" data-testid="new-payment-btn">Nuovo pagamento</button>
          </motion.div>
        )}

        {/* Special order requests — separate from normal orders, no payment */}
        <div className="mt-12" data-testid="special-requests-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-2xl font-bold text-white flex items-center gap-3">
              Ordini Speciali
              {specialRequests.filter((r) => (r.status || "NEW") === "NEW").length > 0 && (
                <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-amber-500/15 text-amber-400 border border-amber-500/40" data-testid="special-new-count">
                  {specialRequests.filter((r) => (r.status || "NEW") === "NEW").length} nuove
                </span>
              )}
            </h2>
          </div>
          {specialRequests.length === 0 ? (
            <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-6 text-center text-zinc-500 text-sm font-body" data-testid="special-empty">
              Nessuna richiesta speciale per ora.
            </div>
          ) : (
            <div className="space-y-3">
              {specialRequests.map((req) => {
                const meta = specialStatusMeta(req.status || "NEW");
                const phoneDigits = String(req.customer_phone || "").replace(/[^\d]/g, "");
                const waUrl = `https://wa.me/${phoneDigits}`;
                return (
                  <div key={req.id} className="bg-[#0A0A0A] border border-white/10 rounded-sm p-5" data-testid={`special-row-${req.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-3 flex-wrap mb-1.5">
                          <span className="text-white font-body font-semibold">{req.customer_name}</span>
                          <a href={waUrl} target="_blank" rel="noopener noreferrer" className="text-[#25D366] font-mono text-xs hover:underline" data-testid={`special-phone-${req.id}`}>{req.customer_phone}</a>
                          <span className={`px-2.5 py-1 rounded-full text-[10px] tracking-[0.15em] uppercase font-bold border ${meta.cls}`} data-testid={`special-status-${req.id}`}>{meta.label}</span>
                        </div>
                        <p className="text-zinc-300 font-body text-sm leading-relaxed whitespace-pre-wrap">{req.description}</p>
                        <p className="text-zinc-600 font-body text-[11px] mt-2">{req.created_at ? new Date(req.created_at).toLocaleString("it-IT") : ""}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <select
                          value={req.status || "NEW"}
                          onChange={(e) => handleUpdateSpecialStatus(req.id, e.target.value)}
                          className="bg-black border border-white/10 rounded-sm text-white text-xs px-2 py-1.5 font-body"
                          data-testid={`special-status-select-${req.id}`}
                        >
                          <option value="NEW" className="bg-black">Nuovo</option>
                          <option value="CONTACTED" className="bg-black">Contattato</option>
                          <option value="DONE" className="bg-black">Completato</option>
                        </select>
                        <a href={waUrl} target="_blank" rel="noopener noreferrer" className="border border-[#25D366]/40 text-[#25D366] hover:bg-[#25D366]/10 font-body font-semibold text-[11px] tracking-[0.15em] uppercase px-3 py-1.5 rounded-sm transition-colors flex items-center gap-1.5" data-testid={`special-contact-btn-${req.id}`}>
                          <MessageCircle className="w-3.5 h-3.5" /> Scrivi
                        </a>
                        <button onClick={() => { if (window.confirm("Eliminare questa richiesta speciale?")) handleDeleteSpecial(req.id); }} className="text-zinc-600 hover:text-red-400 transition-colors p-1.5" data-testid={`special-delete-btn-${req.id}`} aria-label="Elimina">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* History */}
        <div className="mt-12" data-testid="payments-history">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-2xl font-bold text-white">Storico Pagamenti</h2>
            <button onClick={() => fetchHistory(password)} className="text-zinc-400 hover:text-white text-xs tracking-[0.2em] uppercase font-body flex items-center gap-2" data-testid="refresh-history-btn">
              <RefreshCw className={`w-4 h-4 ${historyLoading ? "animate-spin" : ""}`} />
              Aggiorna
            </button>
          </div>

          {/* Search bar */}
          {history.length > 0 && (
            <div className="mb-4 space-y-3">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none" />
                <input
                  type="text"
                  value={historyQuery}
                  onChange={(e) => setHistoryQuery(e.target.value)}
                  placeholder="Cerca per nome cliente o riferimento ordine..."
                  className="w-full pl-11 pr-4 py-3 bg-[#0A0A0A] border border-white/10 rounded-sm text-white font-body text-sm focus:outline-none focus:border-[#009A49] transition-colors placeholder:text-zinc-600"
                  data-testid="history-search-input"
                />
                {historyQuery && (
                  <button
                    onClick={() => setHistoryQuery("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white text-xs tracking-wider uppercase font-body px-2"
                    data-testid="history-search-clear"
                  >
                    Pulisci
                  </button>
                )}
              </div>

              {/* Status filter chips */}
              <div className="flex flex-wrap items-center gap-2" data-testid="status-filter">
                {[
                  { key: "all", label: "Tutti" },
                  { key: "paid", label: "Pagato" },
                  { key: "pending", label: "In Attesa" },
                  { key: "failed", label: "Fallito" },
                ].map((f) => {
                  const active = statusFilter === f.key;
                  const activeClasses = {
                    all: "bg-white text-black border-white",
                    paid: "bg-[#009A49] text-white border-[#009A49]",
                    pending: "bg-amber-500 text-black border-amber-500",
                    failed: "bg-red-500 text-white border-red-500",
                  }[f.key];
                  return (
                    <button
                      key={f.key}
                      onClick={() => setStatusFilter(f.key)}
                      className={`px-4 py-1.5 rounded-full border text-[11px] font-body font-semibold tracking-[0.15em] uppercase transition-colors ${
                        active ? activeClasses : "bg-transparent border-white/10 text-zinc-400 hover:border-white/30 hover:text-white"
                      }`}
                      data-testid={`status-filter-${f.key}`}
                    >
                      {f.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {history.length === 0 ? (
            <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-8 text-center">
              <Clock className="w-8 h-8 text-zinc-600 mx-auto mb-3" />
              <p className="text-zinc-500 font-body text-sm">Nessun pagamento ancora generato</p>
            </div>
          ) : (
            <div className="bg-[#0A0A0A] border border-white/10 rounded-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-white/5 text-zinc-500 text-[10px] tracking-[0.2em] uppercase font-body">
                    <tr>
                      <th className="text-left px-3 py-3">Data</th>
                      <th className="text-left px-3 py-3">Cliente</th>
                      <th className="text-left px-3 py-3">Descrizione</th>
                      <th className="text-right px-3 py-3">Importo</th>
                      <th className="text-center px-3 py-3">Metodo</th>
                      <th className="text-center px-3 py-3">Stato</th>
                      <th className="text-center px-3 py-3 whitespace-nowrap">Azioni</th>
                    </tr>
                  </thead>
                  <tbody className="text-zinc-300 font-body">
                    {(() => {
                      const q = historyQuery.trim().toLowerCase();
                      // Apply status filter chips
                      const matchesFilter = (inv) => {
                        const s = String(inv.status || "").toUpperCase();
                        if (statusFilter === "all") return true;
                        if (statusFilter === "paid") return s === "PAID" || s === "CAPTURED";
                        if (statusFilter === "pending") return s === "CREATED" || s === "AUTHORIZED" || s === "PENDING" || s === "";
                        if (statusFilter === "failed") return ["FAILED", "DECLINED", "EXPIRED", "CANCELLED", "REFUNDED"].includes(s);
                        return true;
                      };
                      let rows = history.filter(matchesFilter);
                      if (q) {
                        rows = rows.filter((inv) =>
                          (inv.customer_name || "").toLowerCase().includes(q) ||
                          (inv.client_ref || "").toLowerCase().includes(q) ||
                          (inv.description || "").toLowerCase().includes(q) ||
                          (inv.payment_method || "").toLowerCase().includes(q)
                        );
                      }
                      if (rows.length === 0) {
                        return (
                          <tr data-testid="history-empty-search">
                            <td colSpan={7} className="px-4 py-8 text-center text-zinc-500 text-sm">
                              {q ? <>Nessun risultato per &ldquo;{historyQuery}&rdquo;</> : "Nessun pagamento in questa categoria"}
                            </td>
                          </tr>
                        );
                      }
                      return rows.map((inv) => {
                      const c = statusColor(inv.status);
                      const dt = inv.created_at ? new Date(inv.created_at).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "-";
                      const amt = inv.total_amount ?? inv.amount ?? 0;
                      return (
                        <tr key={inv.client_ref} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`history-row-${inv.client_ref}`}>
                          <td className="px-3 py-3 text-xs whitespace-nowrap">{dt}</td>
                          <td className="px-3 py-3 whitespace-nowrap">{inv.customer_name || "—"}</td>
                          <td className="px-3 py-3 max-w-xs truncate" title={inv.description}>
                            {inv.link_type === "shipping_only" && (
                              <span className="inline-block mr-2 px-2 py-0.5 rounded-full text-[9px] tracking-[0.1em] uppercase font-bold border bg-cyan-500/15 text-cyan-300 border-cyan-500/40 align-middle" data-testid={`history-shipping-only-${inv.client_ref}`}>Solo Sped.</span>
                            )}
                            {inv.description}
                          </td>
                          <td className="px-3 py-3 text-right font-mono whitespace-nowrap">€{Number(amt).toFixed(2)}</td>
                          <td className="px-3 py-3 text-center">
                            {(() => {
                              const pm = String(inv.payment_method || "stripe").toLowerCase();
                              const map = { stripe: { label: "Stripe", cls: "bg-indigo-500/15 text-indigo-300 border-indigo-500/40" },
                                            wise: { label: "Wise", cls: "bg-cyan-500/15 text-cyan-300 border-cyan-500/40" },
                                            cash: { label: "Contanti", cls: "bg-amber-500/15 text-amber-300 border-amber-500/40" },
                                            other: { label: "Altro", cls: "bg-zinc-500/15 text-zinc-300 border-zinc-500/40" } };
                              const m = map[pm] || map.other;
                              return <span className={`inline-block px-2.5 py-1 rounded-full text-[10px] tracking-[0.15em] uppercase font-bold border whitespace-nowrap ${m.cls}`}>{m.label}</span>;
                            })()}
                          </td>
                          <td className="px-3 py-3 text-center">
                            <span className={`inline-block px-3 py-1 rounded-full text-[10px] tracking-[0.2em] uppercase font-bold border ${c.bg} ${c.text} ${c.border} whitespace-nowrap`}>{statusLabel(inv.status)}</span>
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap">
                            <div className="flex items-center justify-center gap-1">
                              <a
                                href={`${API}/payment/receipt/${inv.client_ref}.pdf`}
                                target="_blank"
                                rel="noreferrer"
                                className="text-[#009A49] hover:text-white transition-colors p-1.5"
                                title="Scarica ricevuta PDF"
                                data-testid={`receipt-btn-${inv.client_ref}`}
                              >
                                <FileText className="w-3.5 h-3.5" />
                              </a>
                              <button
                                onClick={() => setEditingInvoice(inv)}
                                className="text-zinc-400 hover:text-white p-1.5"
                                title="Modifica pagamento"
                                data-testid={`invoice-edit-btn-${inv.client_ref}`}
                              >
                                <Pencil className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => handleDeleteInvoice(inv)}
                                disabled={deletingInvoice === inv.client_ref}
                                className="text-red-400 hover:text-red-300 p-1.5 disabled:opacity-50"
                                title="Elimina pagamento"
                                data-testid={`invoice-delete-btn-${inv.client_ref}`}
                              >
                                {deletingInvoice === inv.client_ref ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    });
                    })()}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {editingOrder && (
        <EditOrderModal
          order={editingOrder}
          onClose={() => setEditingOrder(null)}
          onSave={handleSaveEditedOrder}
        />
      )}
      {editingInvoice && (
        <EditInvoiceModal
          invoice={editingInvoice}
          onClose={() => setEditingInvoice(null)}
          onSave={handleSaveEditedInvoice}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// DiscountInput — free-form % input with quick preset chips + commit on blur/Enter
// ---------------------------------------------------------------------------
function DiscountInput({ value, onCommit, testIdBase, type = "discount" }) {
  const [local, setLocal] = useState(String(value ?? 0));
  useEffect(() => { setLocal(String(value ?? 0)); }, [value]);

  const presets = type === "fee" ? [0, 1.5, 2.5, 3.5, 4.5, 5] : [0, 5, 10, 12.5, 15, 20, 25, 30, 50];
  const max = type === "fee" ? 20 : 100;

  const commit = () => {
    const n = parseFloat(String(local).replace(",", "."));
    if (Number.isNaN(n) || n < 0 || n > max) {
      setLocal(String(value ?? 0));
      return;
    }
    const rounded = Math.round(n * 100) / 100;
    if (rounded === Number(value)) return;
    onCommit(rounded);
  };

  const applyPreset = (p) => {
    setLocal(String(p));
    if (p !== Number(value)) onCommit(p);
  };

  return (
    <div className="space-y-1.5">
      <div className="flex items-stretch gap-1">
        <input
          type="number"
          min="0"
          max={String(max)}
          step="0.5"
          value={local}
          onChange={(e) => setLocal(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === "Enter") { e.currentTarget.blur(); } }}
          className="edit-input flex-1 min-w-0"
          data-testid={`${testIdBase}-input`}
          placeholder="0"
        />
        <span className="px-2 py-2 bg-white/5 border border-white/10 text-zinc-400 text-sm font-mono rounded-sm">%</span>
      </div>
      <div className="flex flex-wrap gap-1">
        {presets.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => applyPreset(p)}
            className={`px-2 py-0.5 rounded-full border text-[10px] font-body tracking-wide transition-colors ${
              Number(value) === p
                ? "bg-[#009A49] border-[#009A49] text-white"
                : "border-white/10 text-zinc-400 hover:text-white hover:border-white/30"
            }`}
            data-testid={`${testIdBase}-preset-${p}`}
          >
            {p === 0 ? "0" : p}%
          </button>
        ))}
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// EditOrderModal — modifica in-place cliente + articoli (categoria, taglia,
// quantità, personalizzazione, patch)
// ---------------------------------------------------------------------------
const CATEGORIES_META = [
  { key: "football", label: "Calcio 26/27", price: 40 },
  { key: "retro", label: "Retro", price: 50 },
  { key: "kids", label: "Bambini", price: 35 },
  { key: "nba", label: "NBA", price: 50 },
  { key: "nbaLegends", label: "NBA Legend", price: 60 },
  { key: "tracksuit", label: "Tuta", price: 65 },
  { key: "f1tee", label: "T-shirt F1", price: 50 },
  { key: "f1polo", label: "Polo F1", price: 60 },
];
const ADULT_SIZES = ["S", "M", "L", "XL", "XXL", "XXXL"];
const KIDS_SIZES = ["XXS", "XS", "S", "M", "L", "XL", "XXL"];
const PATCH_META = [
  { key: "none", label: "Nessuna patch" },
  { key: "champions", label: "Champions League" },
  { key: "campionato", label: "Campionato" },
];

function EditOrderModal({ order, onClose, onSave }) {
  const [form, setForm] = useState(() => ({
    id: order.id,
    customer_name: order.customer_name || "",
    customer_email: order.customer_email || "",
    customer_phone: order.customer_phone || "",
    country: order.country || "",
    state: order.state || "",
    city: order.city || "",
    address: order.address || "",
    zip: order.zip || "",
    notes: order.notes || "",
    items: (order.items || []).map((it) => ({
      category: it.category,
      jersey: it.jersey || "",
      size: it.size || "M",
      quantity: it.quantity || 1,
      personalization: it.personalization || "",
      patch: it.patch || "none",
      patch_label: it.patch_label || "",
    })),
  }));
  const [saving, setSaving] = useState(false);

  const patchAllowed = (cat) => ["football", "retro", "kids"].includes(cat);

  const updateItem = (idx, key, value) => {
    setForm((prev) => {
      const items = prev.items.slice();
      items[idx] = { ...items[idx], [key]: value };
      // If category changes -> reset patch if not allowed, reset size to M
      if (key === "category") {
        if (!patchAllowed(value)) { items[idx].patch = "none"; items[idx].patch_label = ""; }
        items[idx].size = "M";
      }
      if (key === "patch") {
        const pm = PATCH_META.find((p) => p.key === value);
        items[idx].patch_label = pm && pm.key !== "none" ? pm.label : "";
      }
      return { ...prev, items };
    });
  };

  const addItemLine = () => {
    setForm((prev) => ({
      ...prev,
      items: [...prev.items, { category: "football", jersey: "", size: "M", quantity: 1, personalization: "", patch: "none", patch_label: "" }],
    }));
  };

  const removeItemLine = (idx) => {
    setForm((prev) => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }));
  };

  const grossPreview = form.items.reduce((sum, it) => {
    const meta = CATEGORIES_META.find((c) => c.key === it.category);
    const price = meta ? meta.price : 0;
    return sum + price * (parseInt(it.quantity, 10) || 0);
  }, 0);
  const totalQty = form.items.reduce((s, it) => s + (parseInt(it.quantity, 10) || 0), 0);
  const willDiscount = totalQty >= 2;
  const discount = willDiscount ? Math.round(grossPreview * 10) / 100 : 0;
  const baseAfterDiscount = grossPreview - discount;

  const canSave = form.customer_name.trim() && form.customer_phone.trim() && form.items.length > 0 &&
                  form.items.every((it) => it.jersey.trim() && it.category);

  const doSave = async () => {
    if (!canSave || saving) return;
    setSaving(true);
    try {
      await onSave(form);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[200] bg-black/80 backdrop-blur-sm flex items-start md:items-center justify-center overflow-y-auto p-4"
      onClick={onClose}
      data-testid="edit-order-modal"
    >
      <div
        className="bg-[#0A0A0A] border border-white/10 rounded-sm w-full max-w-3xl my-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#009A49] font-body">Modifica Ordine</p>
            <h3 className="font-heading text-white text-xl tracking-wide mt-1">{order.customer_name}</h3>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white p-2" data-testid="edit-order-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
          {/* Customer */}
          <div>
            <p className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 font-body mb-2">Cliente</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input type="text" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} placeholder="Nome" className="edit-input" data-testid="edit-customer-name" />
              <input type="email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} placeholder="Email" className="edit-input" data-testid="edit-customer-email" />
              <input type="tel" value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} placeholder="Telefono" className="edit-input" data-testid="edit-customer-phone" />
              <input type="text" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} placeholder="Paese" className="edit-input" />
              <input type="text" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} placeholder="Provincia" className="edit-input" />
              <input type="text" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Città" className="edit-input" />
              <input type="text" value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} placeholder="CAP" className="edit-input" />
              <input type="text" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Indirizzo" className="edit-input" />
            </div>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Note interne / cliente" rows={2} className="edit-input w-full mt-3 resize-none" />
          </div>

          {/* Items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 font-body">Articoli ({form.items.length})</p>
              <button
                type="button"
                onClick={addItemLine}
                className="text-[#009A49] hover:text-[#00b356] font-body text-xs tracking-wider uppercase flex items-center gap-1"
                data-testid="edit-add-item"
              >
                <Plus className="w-3.5 h-3.5" />
                Aggiungi
              </button>
            </div>
            <div className="space-y-3">
              {form.items.map((it, idx) => {
                const meta = CATEGORIES_META.find((c) => c.key === it.category);
                const sizes = it.category === "kids" ? KIDS_SIZES : ADULT_SIZES;
                return (
                  <div key={idx} className="bg-black/40 border border-white/10 rounded-sm p-3 space-y-2" data-testid={`edit-item-${idx}`}>
                    <div className="flex items-start gap-2">
                      <input
                        type="text"
                        value={it.jersey}
                        onChange={(e) => updateItem(idx, "jersey", e.target.value)}
                        placeholder="Nome maglia"
                        className="edit-input flex-1"
                        data-testid={`edit-item-jersey-${idx}`}
                      />
                      <button
                        onClick={() => removeItemLine(idx)}
                        className="text-red-400 hover:text-red-300 p-2"
                        aria-label="Rimuovi articolo"
                        data-testid={`edit-item-remove-${idx}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 items-center">
                      <select
                        value={it.category}
                        onChange={(e) => updateItem(idx, "category", e.target.value)}
                        className="edit-input"
                        data-testid={`edit-item-category-${idx}`}
                      >
                        {CATEGORIES_META.map((c) => (
                          <option key={c.key} value={c.key} className="bg-black">{c.label} — €{c.price}</option>
                        ))}
                      </select>
                      <select value={it.size} onChange={(e) => updateItem(idx, "size", e.target.value)} className="edit-input">
                        {sizes.map((s) => <option key={s} value={s} className="bg-black">{s}</option>)}
                      </select>
                      <input
                        type="number"
                        min="1"
                        max="99"
                        value={it.quantity}
                        onChange={(e) => updateItem(idx, "quantity", e.target.value)}
                        className="edit-input"
                        data-testid={`edit-item-qty-${idx}`}
                      />
                      <div className="text-right font-mono text-xs text-zinc-400">
                        €{meta ? (meta.price * (parseInt(it.quantity, 10) || 0)).toFixed(2) : "0.00"}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <input
                        type="text"
                        value={it.personalization}
                        onChange={(e) => updateItem(idx, "personalization", e.target.value)}
                        placeholder="Personalizzazione (nome + numero)"
                        className="edit-input"
                      />
                      {patchAllowed(it.category) ? (
                        <select value={it.patch} onChange={(e) => updateItem(idx, "patch", e.target.value)} className="edit-input" data-testid={`edit-item-patch-${idx}`}>
                          {PATCH_META.map((p) => <option key={p.key} value={p.key} className="bg-black">{p.label}</option>)}
                        </select>
                      ) : (
                        <div className="edit-input flex items-center text-zinc-600 text-xs italic">Patch non disponibile</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Preview totals */}
          <div className="border-t border-white/5 pt-4 space-y-1 text-sm font-body">
            <div className="flex justify-between text-zinc-400"><span>Subtotale</span><span className="font-mono">€{grossPreview.toFixed(2)}</span></div>
            {willDiscount && (
              <div className="flex justify-between text-[#009A49]"><span>Sconto multi-articolo (−10%)</span><span className="font-mono">−€{discount.toFixed(2)}</span></div>
            )}
            <div className="flex justify-between text-white font-semibold pt-1 border-t border-white/5 mt-1"><span>Base pagamento</span><span className="font-mono">€{baseAfterDiscount.toFixed(2)}</span></div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-white/10">
          <button onClick={onClose} className="text-zinc-400 hover:text-white font-body text-xs tracking-[0.2em] uppercase px-4 py-2.5">Annulla</button>
          <button
            onClick={doSave}
            disabled={!canSave || saving}
            className="bg-[#009A49] hover:bg-[#00b356] disabled:opacity-50 disabled:cursor-not-allowed text-white font-body font-semibold text-xs tracking-[0.15em] uppercase px-5 py-2.5 rounded-sm transition-colors flex items-center gap-2"
            data-testid="edit-order-save"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Salva Modifiche
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// EditInvoiceModal — modifica cliente/descrizione/stato di un pagamento
// ---------------------------------------------------------------------------
const INVOICE_STATUS_OPTIONS = [
  { key: "CREATED", label: "In Attesa" },
  { key: "PAID", label: "Pagato" },
  { key: "FAILED", label: "Fallito" },
  { key: "EXPIRED", label: "Scaduto" },
  { key: "CANCELLED", label: "Annullato" },
  { key: "REFUNDED", label: "Rimborsato" },
];

function EditInvoiceModal({ invoice, onClose, onSave }) {
  const [form, setForm] = useState({
    client_ref: invoice.client_ref,
    status: (invoice.status || "CREATED").toUpperCase(),
    customer_name: invoice.customer_name || "",
    customer_email: invoice.customer_email || "",
    customer_phone: invoice.customer_phone || "",
    description: invoice.description || "",
  });
  const [saving, setSaving] = useState(false);

  const doSave = async () => {
    if (saving) return;
    setSaving(true);
    try { await onSave(form); } finally { setSaving(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[200] bg-black/80 backdrop-blur-sm flex items-center justify-center overflow-y-auto p-4"
      onClick={onClose}
      data-testid="edit-invoice-modal"
    >
      <div className="bg-[#0A0A0A] border border-white/10 rounded-sm w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#009A49] font-body">Modifica Pagamento</p>
            <h3 className="font-heading text-white text-xl tracking-wide mt-1 font-mono">{invoice.client_ref}</h3>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white p-2" data-testid="edit-invoice-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-[10px] tracking-[0.25em] uppercase text-zinc-500 font-body mb-1.5">Stato</label>
            <select
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
              className="edit-input w-full"
              data-testid="edit-invoice-status"
            >
              {INVOICE_STATUS_OPTIONS.map((o) => (
                <option key={o.key} value={o.key} className="bg-black">{o.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-[10px] tracking-[0.25em] uppercase text-zinc-500 font-body mb-1.5">Cliente</label>
            <input
              type="text"
              value={form.customer_name}
              onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
              placeholder="Nome cliente"
              className="edit-input w-full"
              data-testid="edit-invoice-customer-name"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input
              type="email"
              value={form.customer_email}
              onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
              placeholder="Email"
              className="edit-input"
            />
            <input
              type="tel"
              value={form.customer_phone}
              onChange={(e) => setForm({ ...form, customer_phone: e.target.value })}
              placeholder="Telefono"
              className="edit-input"
            />
          </div>

          <div>
            <label className="block text-[10px] tracking-[0.25em] uppercase text-zinc-500 font-body mb-1.5">Descrizione</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              className="edit-input w-full resize-none"
              data-testid="edit-invoice-description"
            />
          </div>

          <p className="text-xs text-zinc-500 font-body italic">
            L&apos;importo del pagamento non è modificabile: se serve cambiarlo, elimina questo pagamento e genera un nuovo link dall&apos;ordine collegato.
          </p>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-white/10">
          <button onClick={onClose} className="text-zinc-400 hover:text-white font-body text-xs tracking-[0.2em] uppercase px-4 py-2.5">Annulla</button>
          <button
            onClick={doSave}
            disabled={saving}
            className="bg-[#009A49] hover:bg-[#00b356] disabled:opacity-50 disabled:cursor-not-allowed text-white font-body font-semibold text-xs tracking-[0.15em] uppercase px-5 py-2.5 rounded-sm transition-colors flex items-center gap-2"
            data-testid="edit-invoice-save"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Salva Modifiche
          </button>
        </div>
      </div>
    </div>
  );
}

